import type {
  WebCarouselFindOneContentItem,
  WebCarouselFindOneLiveChannelItem,
} from '@bldsoft/ovp-api-types'
import {
  MediaTypes,
  OVPBannerType,
  type OVPBannerCarousel,
  type OVPBannerContent,
  type OVPCarouselType,
} from '@setplex/tria-api'
import { toDate } from '../../tools'
import { format as formatCollection } from '../collections/format'
import type { ApiCollection } from '../collections/index.h'
import { format as formatContent } from '../content/format'
import type { ApiContent } from '../content/index.h'
import { format as formatTag } from '../tags/format'
import type { ApiTag } from '../tags/index.h'
import type { ApiOVPBannerCarousel, ApiOVPBannerContent } from './index.h'

const formatCarouselContentItem = (
  item: WebCarouselFindOneContentItem
): OVPBannerContent => ({
  type: OVPBannerType.CONTENT,
  sortOrder: undefined,
  id: item.id,
  title: item.title,
  description: item.description,
  duration: String(item.duration),
  userRating: item.userRating,
  ageRating: item.ageRating,
  poster: item.horizontalImage,
  banner: item.banner,
  createDate: toDate(item.createDate),
  updateDate: toDate(item.updateDate),
  isFavorite: item.isFavorite,
  newOtherTags: [],
  contentType: MediaTypes.VIDEO,
})

const formatCarouselLiveChannelItem = (
  item: WebCarouselFindOneLiveChannelItem
): OVPBannerContent => ({
  type: OVPBannerType.LIVE_CHANNEL,
  sortOrder: item.sortOrder,
  id: item.id,
  channelId: item.channelId,
  title: item.title,
  logo: item.logo,
  number: item.number,
  banner: item.banner,
})

const formatBanner = (item: ApiOVPBannerContent): OVPBannerContent => {
  if ('type' in item && item.type === OVPBannerType.LIVE_CHANNEL) {
    return formatCarouselLiveChannelItem(
      item as WebCarouselFindOneLiveChannelItem
    )
  }

  if ('type' in item && item.type === OVPBannerType.TAGS) {
    return {
      type: item.type,
      sortOrder: item.sortOrder,
      ...formatTag(item as ApiTag),
    }
  }

  if ('type' in item && item.type === OVPBannerType.COLLECTIONS) {
    return {
      type: item.type,
      sortOrder: item.sortOrder,
      ...formatCollection(item as ApiCollection),
    }
  }

  if ('horizontalImage' in item) {
    return formatCarouselContentItem(item as WebCarouselFindOneContentItem)
  }

  return {
    type: OVPBannerType.CONTENT,
    sortOrder: 'sortOrder' in item ? item.sortOrder : undefined,
    ...formatContent(item as ApiContent),
  }
}

const mapCarouselType = (type: string | undefined): OVPCarouselType | null => {
  switch (type) {
    case OVPBannerType.LIVE_CHANNEL:
      return OVPBannerType.LIVE_CHANNEL
    case OVPBannerType.CONTENT:
      return OVPBannerType.CONTENT
    default:
      return null
  }
}

export const format = ({
  id,
  title,
  autoScroll,
  autoScrollInterval,
  items,
  ...carousel
}: ApiOVPBannerCarousel): OVPBannerCarousel => ({
  id,
  title,
  type: mapCarouselType('type' in carousel ? carousel.type : undefined),
  autoScroll,
  autoScrollInterval,
  items: items?.length
    ? [...items].map((item) => formatBanner(item as ApiOVPBannerContent))
    : [],
})

export { formatBanner }
